import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { ClearInitialContentController } from "@/components/meta/clear_initial_content_controller";
import { I18n } from "@/i18n/i18n";
import dayjs from "dayjs";

/**
 * A web component that displays a relative time string and auto-updates every 60 seconds.
 * This keeps timestamps fresh when pages are served from cache.
 *
 * @element d-time-ago
 *
 * @prop {string} datetime - ISO 8601 timestamp (required)
 * @prop {string} title - Optional tooltip override. Defaults to dayjs LLLL format.
 * @prop {boolean} autoUpdate - When true (default), updates every 60 seconds.
 * @prop {boolean} compact - When true, renders abbreviated units ("5 min") instead of "5 minutes ago".
 */
@customElement("d-time-ago")
export class TimeAgo extends DodonaElement {
    clearInitialContent = new ClearInitialContentController(this);

    @property({ type: String })
    datetime: string;

    @property({ type: String })
    title: string;

    @property({
        attribute: "auto-update",
        converter: { fromAttribute: (value: string | null): boolean => value !== "false" },
    })
    autoUpdate = true;

    @property({ type: Boolean })
    compact = false;

    private static readonly UPDATE_INTERVAL_MS = 60000;

    private intervalId: ReturnType<typeof setInterval> | undefined;

    connectedCallback(): void {
        super.connectedCallback();
        if (this.autoUpdate) {
            this.intervalId = setInterval(() => this.requestUpdate(), TimeAgo.UPDATE_INTERVAL_MS);
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        clearInterval(this.intervalId);
        this.intervalId = undefined;
    }

    /**
     * A coarser, shorter alternative to dayjs' fromNow(). Anything below a minute collapses
     * into a single "just now", and anything older than four weeks becomes an absolute date
     * instead of a vague "a month ago". Counts are rounded like fromNow does.
     */
    private compactFromNow(d: dayjs.Dayjs): string {
        const now = dayjs();
        const minutes = Math.max(now.diff(d, "minute"), 0);
        if (minutes < 1) {
            return I18n.t("js.time_ago_compact.now");
        }
        if (minutes < 60) {
            return I18n.t("js.time_ago_compact.minutes", { count: minutes });
        }

        const hours = Math.round(now.diff(d, "hour", true));
        if (hours < 24) {
            return I18n.t("js.time_ago_compact.hours", { smart_count: hours });
        }

        const days = Math.round(now.diff(d, "day", true));
        if (days < 7) {
            return I18n.t("js.time_ago_compact.days", { smart_count: days });
        }
        if (days < 11) {
            return I18n.t("js.time_ago_compact.last_week");
        }
        if (days < 28) {
            return I18n.t("js.time_ago_compact.weeks", { count: Math.round(days / 7) });
        }

        const sameYear = now.year() === d.year();
        return d.format(sameYear ? I18n.t("js.time_ago_compact.date") : I18n.t("js.time_ago_compact.date_with_year"));
    }

    render(): TemplateResult {
        const d = dayjs(this.datetime).locale(I18n.locale());
        const relativeString = this.compact ? this.compactFromNow(d) : d.fromNow();
        const tooltipText = this.title || d.format("LLLL");
        return html`<time datetime="${this.datetime}" title="${tooltipText}">${relativeString}</time>`;
    }
}
